import {
  BEFORE_SUBMISSION,
  isTerminalStage,
  resolveStatus,
  stagesFor,
  type StageInfo,
} from '@constants/applicationStatus';

/**
 * How the stage stepper draws a candidate, and what the one-click move offers.
 *
 * Pure reads of `constants/applicationStatus.ts`, which is on the "Never change" list: nothing here
 * adds a stage or a rule, it only decides how the existing ones are drawn.
 */

export interface StageProgress {
  /** The main path, in order. Closed is never on it: it is an exit, not a step. */
  path: StageInfo[];
  /** The stage the candidate is on, as an index into `path`; -1 when they are off it. */
  currentIndex: number;
  /**
   * Off the path: Closed, with the reason (Client Rejected, Candidate Withdrew, and so on) and the
   * path stage they had reached, which the stepper marks as where they stopped rather than as
   * where they are. Null while the candidate is on the path.
   */
  exit: { label: string; reason: string; stoppedIndex: number } | null;
}

interface ProgressApp {
  status: string;
  subStatus?: string | null;
  closedReason?: string | null;
  history?: Array<{ status: string; previousStatus?: string | null }>;
}

export const stageProgress = (app: ProgressApp, managed: boolean): StageProgress => {
  const path = stagesFor(managed).filter((s) => s.label !== 'Closed');
  const resolved = resolveStatus(app.status, app.subStatus, app.closedReason);
  const current = resolved.stage?.label ?? '';
  if (current !== 'Closed') {
    return { path, currentIndex: path.findIndex((s) => s.label === current), exit: null };
  }
  // Where they were when they were closed: the last move INTO Closed names the stage it left.
  const last = [...(app.history ?? [])].reverse().find((h) => resolveStatus(h.status).stage?.label === 'Closed');
  const before = resolveStatus(last?.previousStatus).stage?.label ?? '';
  return {
    path,
    currentIndex: -1,
    exit: {
      label: 'Closed',
      reason: resolved.closedReason,
      stoppedIndex: path.findIndex((s) => s.label === before),
    },
  };
};

export interface NextStageContext {
  managed: boolean;
  /** False when a managed candidate belongs to another recruiter. */
  canMove: boolean;
  /** The job is On Hold, Filled or Closed. */
  locked: boolean;
  superAdmin: boolean;
  /**
   * The candidate has not confirmed we may represent them. Submitted and everything after it sit
   * behind that gate on the backend, and the card already offers the consent action instead.
   */
  consentPending: boolean;
}

/**
 * The stage "Move to next stage" would send the candidate to, or null when there is no safe one.
 *
 * Offered only where the backend would accept it without a detail or a reason, so the button is
 * never a refusal waiting to happen:
 * - never into Closed (that needs a reason, and it is an exit, not a next step);
 * - never from Joined or Closed (terminal; reopening is its own Super Admin action);
 * - never on a paused or ended job, unless a Super Admin, matching the stage select;
 * - never past the consent gate while consent is outstanding;
 * - never from a stage the job does not use (a legacy record on a self-serve job), where "next" is
 *   not defined.
 */
export const nextStage = (status: string | null | undefined, ctx: NextStageContext): string | null => {
  if (!ctx.canMove) return null;
  const current = resolveStatus(status).stage?.label ?? '';
  if (!current || isTerminalStage(current)) return null;
  if (ctx.locked && !ctx.superAdmin) return null;
  const path = stagesFor(ctx.managed).filter((s) => s.label !== 'Closed');
  const i = path.findIndex((s) => s.label === current);
  if (i < 0 || i >= path.length - 1) return null;
  const next = path[i + 1].label;
  if (ctx.consentPending && !BEFORE_SUBMISSION.includes(next)) return null;
  return next;
};
